'use client';

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangleIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { useEffect, useState } from "react";
import { usePathname } from "next/navigation";
import { useTranslations } from "next-intl";
import { useBrowserAuthUser } from "@/utils/supabase/use-browser-auth-user";

const DISMISSAL_KEY = "password-setup-banner-dismissed";
const DISMISSAL_DURATION = 4 * 60 * 60 * 1000; // 4 hours
const AUTH_PATH_SEGMENTS = ['/sign-in', '/sign-up', '/forgot-password', '/reset-password', '/continue-signup'];

function isAuthPath(path: string): boolean {
  return AUTH_PATH_SEGMENTS.some((segment) => path.includes(segment));
}

export function PasswordSetupBanner() {
  const [isDismissedRecently, setIsDismissedRecently] = useState(false);
  const pathname = usePathname();
  const { user, isLoading } = useBrowserAuthUser();
  const t = useTranslations('auth');
  const browserPathname = typeof window === 'undefined' ? null : window.location.pathname;
  const pathCandidates = [pathname, browserPathname].filter(
    (value): value is string => typeof value === 'string' && value.length > 0,
  );
  const isAuthPage = pathCandidates.length === 0 || pathCandidates.some((path) => isAuthPath(path));

  useEffect(() => {
    const dismissedAt = localStorage.getItem(DISMISSAL_KEY);
    if (!dismissedAt) {
      setIsDismissedRecently(false);
      return;
    }

    const parsedDismissedAt = Number.parseInt(dismissedAt, 10);
    if (Number.isNaN(parsedDismissedAt)) {
      localStorage.removeItem(DISMISSAL_KEY);
      setIsDismissedRecently(false);
      return;
    }

    const timeSinceDismissal = Date.now() - parsedDismissedAt;
    if (timeSinceDismissal < DISMISSAL_DURATION) {
      setIsDismissedRecently(true);
      return;
    }

    localStorage.removeItem(DISMISSAL_KEY);
    setIsDismissedRecently(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const needsPasswordSetup =
    !!user &&
    !!user.email &&
    !user.is_anonymous &&
    !!user.user_metadata?.manually_linked_passwordless;

  const showBanner = !isAuthPage && !isLoading && needsPasswordSetup && !isDismissedRecently;

  const handleDismiss = () => {
    localStorage.setItem(DISMISSAL_KEY, Date.now().toString());
    setIsDismissedRecently(true);
  };

  // Don't show on reset-password page or when not needed
  if (isAuthPage || !showBanner) {
    return null;
  }

  return (
    <div className="w-full">
      <div className="max-w-screen-2xl mx-auto px-5 py-2">
        <Alert className="border-amber-300 bg-amber-100 dark:border-amber-900 dark:bg-amber-950/95 backdrop-blur-sm">
          <AlertTriangleIcon className="h-4 w-4 !text-amber-900 dark:!text-white" />
          <AlertTitle className="text-amber-900 dark:text-white text-sm">{t('passwordSetupBanner.title')}</AlertTitle>
          <AlertDescription className="text-amber-900 dark:text-white/80 text-sm flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
            <span>{t('passwordSetupBanner.message')}</span>
            <div className="flex items-center gap-2 shrink-0">
              <Button asChild variant="outline" size="sm" className="border-amber-700 bg-amber-700 text-white hover:bg-amber-800 hover:border-amber-800 hover:text-white dark:bg-white/10 dark:border-white/20 dark:text-white dark:hover:bg-white/20 dark:hover:text-white">
                <Link href="/reset-password?redirect_to=/translate">
                  {t('passwordSetupBanner.action')}
                </Link>
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="border-amber-700 bg-transparent text-amber-900 hover:bg-amber-700/50 hover:text-white hover:border-amber-700 dark:bg-white/10 dark:border-white/20 dark:text-white dark:hover:bg-white/20 dark:hover:text-white"
                onClick={handleDismiss}>
                {t('passwordSetupBanner.dismiss')}
              </Button>
            </div>
          </AlertDescription>
        </Alert>
      </div>
    </div>
  );
}
